import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";

// Matches Diamond-PO-Prototype-v3.html's stoneCard() action cell exactly
// (status pill + countdown + Hold&basket/Remove/Unavailable button), ported
// into a single antd Table column cell rather than the prototype's card layout
// -- Task 2167 only adds a column to the existing DiamondSearch.js/ColorDiamondSearch.js
// tables, it doesn't redesign them (per the epic handover notes).

const asUtc = (iso) => new Date(iso?.endsWith("Z") ? iso : iso + "Z");

const cdText = (expiresUtc) => {
  if (!expiresUtc) return "";
  const ms = asUtc(expiresUtc).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const m = Math.floor(ms / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

const cdColor = (expiresUtc) => {
  if (!expiresUtc) return "#6b7689";
  const m = (asUtc(expiresUtc).getTime() - Date.now()) / 60000;
  return m < 15 ? "#e2574c" : m < 60 ? "#e8a33d" : "#6b7689";
};

const pillStyle = (status) => ({
  display: "inline-flex",
  alignItems: "center",
  fontSize: 11,
  fontWeight: 700,
  padding: "3px 9px",
  borderRadius: 20,
  whiteSpace: "nowrap",
  background: status === "HELD" ? "#fdf3e3" : "#e7f6ef",
  color: status === "HELD" ? "#a3701a" : "#1c8b57",
});

export const StoneTradeActions = ({ dInventoryId, availabilityStatus, myDealerId, holdInfo, onChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0);
  // Solr's AvailabilityStatus (the `availabilityStatus` prop) only reflects a hold after the
  // async per-stone reindex completes -- too slow for the button to wait on. This local override
  // reflects what WE just did in this session, so the UI flips immediately on success.
  const [localOverride, setLocalOverride] = useState(null); // null | { held, isMine, expiresUtc }

  // holdInfo comes from the DB (GetHoldStatusForStones) and is authoritative; availabilityStatus
  // comes from Solr and can lag behind it (per-stone reindex is unreliable -- see Task 2167 notes),
  // so either signal being "held" is enough, and the DB is trusted over a stale Solr "available".
  const isHeld = localOverride ? localOverride.held : availabilityStatus === "HELD" || Boolean(holdInfo);
  const isMine = localOverride
    ? localOverride.isMine
    : isHeld && holdInfo && String(holdInfo.RetailerDealerID) === String(myDealerId);
  const expiresUtc = localOverride ? localOverride.expiresUtc : holdInfo?.ExpiresUtc;
  // A hold already attached to a memo/PO document can't be released from the search page
  // (ReleaseDiamondHold refuses it server-side too) -- it only comes off via the document's
  // own lifecycle (CancelUnsent/Ship/Decline).
  // Note: a NULL DiamondDocID serializes as {} (not null) through the DataTable->JSON path,
  // and {} is truthy in JS -- check the type, not just truthiness.
  const isLocked = isMine && !localOverride && typeof holdInfo?.DiamondDocID === "number";

  useEffect(() => {
    // A basket change made elsewhere (e.g. BasketTray's "Release all") doesn't go through this
    // component's own handlePlace/handleRemove, so its localOverride would otherwise go stale --
    // drop it and fall back to fresh props once the parent re-fetches holdInfo.
    const handler = () => setLocalOverride(null);
    window.addEventListener("basketChanged", handler);
    return () => window.removeEventListener("basketChanged", handler);
  }, []);

  useEffect(() => {
    if (!isHeld) return;
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, [isHeld]);

  const handlePlace = () => {
    setLoading(true);
    PurchaseOrderService.PlaceDiamondHold({ DInventoryID: String(dInventoryId), DealerID: String(myDealerId) })
      .then((response) => {
        setLoading(false);
        if (response.data.status === true) {
          NotificationManager.success(t("Stone held and added to your basket"));
          setLocalOverride({ held: true, isMine: true, expiresUtc: new Date(Date.now() + 120 * 60000).toISOString() });
          window.dispatchEvent(new CustomEvent("basketChanged"));
        } else {
          NotificationManager.error(t(response.data.message || "No longer available"));
          // "Different supplier" means the stone itself is still genuinely available -- only
          // our own request was rejected for basket-composition reasons, not a real conflict.
          if (!(response.data.message || "").startsWith("Different supplier")) {
            setLocalOverride({ held: true, isMine: false, expiresUtc: null });
          }
        }
        onChange && onChange();
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not place hold"));
      });
  };

  const handleRemove = () => {
    setLoading(true);
    PurchaseOrderService.ReleaseDiamondHold({ DInventoryID: String(dInventoryId), DealerID: String(myDealerId) })
      .then((response) => {
        setLoading(false);
        if (response.data.status === true) {
          NotificationManager.success(t("Removed from your basket"));
          setLocalOverride({ held: false, isMine: false, expiresUtc: null });
          window.dispatchEvent(new CustomEvent("basketChanged"));
        } else {
          NotificationManager.error(t(response.data.message || "Could not remove hold"));
        }
        onChange && onChange();
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not remove hold"));
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <span style={pillStyle(isHeld ? "HELD" : "AVAILABLE")}>{isHeld ? t("On hold") : t("Available")}</span>
      {isHeld && (
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontWeight: 700,
            fontSize: 11,
            color: cdColor(expiresUtc),
            whiteSpace: "nowrap",
          }}
        >
          {isMine ? t("your hold") : t("held by another buyer")}
          {expiresUtc ? ` · ${cdText(expiresUtc)} ${t("left")}` : ""}
        </span>
      )}
      {isHeld && !isMine ? (
        <Button size="small" disabled>
          {t("Unavailable")}
        </Button>
      ) : isHeld && isLocked ? (
        <Button size="small" disabled title={t("Already part of a request or order")}>
          {t("Requested")}
        </Button>
      ) : isHeld && isMine ? (
        <Button size="small" loading={loading} onClick={handleRemove}>
          {t("Remove")}
        </Button>
      ) : (
        <Button
          size="small"
          type="primary"
          loading={loading}
          onClick={handlePlace}
          style={{ background: "#3b5bdb", borderColor: "#3b5bdb" }}
        >
          {t("Hold & add to basket")}
        </Button>
      )}
    </div>
  );
};

export default StoneTradeActions;
